import type React from 'react';

const UserGuide: React.FC = () => {
  return (
    <div className="mx-auto p-8 bg-white rounded-lg">
      <div className="text-center mb-12 pb-8 border-b-2 border-zinc-200">
        <h1 className="text-4xl font-bold text-zinc-900 mb-4">Search Admin App - User Guide</h1>
        <p className="text-xl text-zinc-500 font-normal">
          Complete guide for managing OpenSearch configuration and promoted results
        </p>
      </div>

      <div className="leading-relaxed text-zinc-700">
        <section className="bg-zinc-50 border border-zinc-200 rounded-lg p-6 mb-8">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 border-b-0 pb-0">Table of Contents</h2>
          <nav className="text-sm">
            <ol className="list-decimal pl-6 m-0">
              <li className="my-2">
                <a href="#overview" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Overview
                </a>
              </li>
              <li className="my-2">
                <a href="#getting-started" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Getting Started
                </a>
              </li>
              <li className="my-2">
                <a href="#search-weights" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Search Weights Management
                </a>
                <ul className="list-disc pl-6 mt-2 mb-2">
                  <li className="my-2">
                    <a
                      href="#search-weights-overview"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Overview
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#available-content-types"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Available Content Types
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#configuring-weights"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Configuring Weights
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#weight-color-legend"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Weight Color Legend
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#search-weights-import-export"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Import/Export JSON
                    </a>
                  </li>
                </ul>
              </li>
              <li className="my-2">
                <a href="#record-boosting" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Record Boosting Management
                </a>
                <ul className="list-disc pl-6 mt-2 mb-2">
                  <li className="my-2">
                    <a
                      href="#record-boosting-overview"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Overview
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#how-record-boosting-works"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      How Record Boosting Works
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#configuring-record-boosting"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Configuring Record Boosting
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#boost-value-guidelines"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Boost Value Guidelines
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#strategic-use-cases"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Strategic Use Cases
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#record-boosting-import-export"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Import/Export JSON
                    </a>
                  </li>
                </ul>
              </li>
              <li className="my-2">
                <a href="#fuzziness-settings" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Fuzziness Settings
                </a>
                <ul className="list-disc pl-6 mt-2 mb-2">
                  <li className="my-2">
                    <a
                      href="#fuzziness-overview"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Overview
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#what-is-fuzziness"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      What is Fuzziness?
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#fuzziness-presets"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Available Presets
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#configuring-fuzziness"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Configuring Fuzziness
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#fuzziness-best-practices"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Best Practices
                    </a>
                  </li>
                </ul>
              </li>
              <li className="my-2">
                <a href="#search-modes" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Search Modes Configuration
                </a>
                <ul className="list-disc pl-6 mt-2 mb-2">
                  <li className="my-2">
                    <a
                      href="#search-modes-overview"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Overview
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#general-search-mode"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      General Search Mode
                    </a>
                  </li>
                  <li className="my-2">
                    <a href="#research-mode" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      Research Mode
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#configuring-search-modes"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Configuring Search Modes
                    </a>
                  </li>
                </ul>
              </li>
              <li className="my-2">
                <a href="#promoted-results" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Results Management
                </a>
                <ul className="list-disc pl-6 mt-2 mb-2">
                  <li className="my-2">
                    <a
                      href="#promoted-results-overview"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Overview
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#keyword-matching"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Keyword Matching Behavior
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#creating-promoted-results"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Creating Managed Results
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#force-result-feature"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Force Result Feature
                    </a>
                  </li>
                  <li className="my-2">
                    <a href="#bulk-operations" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      Bulk Operations
                    </a>
                  </li>
                </ul>
              </li>
              <li className="my-2">
                <a
                  href="#advertisement-management"
                  className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                >
                  Advertisement Management
                </a>
                <ul className="list-disc pl-6 mt-2 mb-2">
                  <li className="my-2">
                    <a
                      href="#advertisement-overview"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Overview
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#advertisement-types"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Advertisement Types
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#creating-advertisement"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Creating an Advertisement
                    </a>
                  </li>
                  <li className="my-2">
                    <a href="#utm-tracking" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      UTM Tracking
                    </a>
                  </li>
                </ul>
              </li>
              <li className="my-2">
                <a href="#gtm-integration" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Google Tag Manager (GTM) Integration
                </a>
                <ul className="list-disc pl-6 mt-2 mb-2">
                  <li className="my-2">
                    <a href="#gtm-overview" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      Overview
                    </a>
                  </li>
                  <li className="my-2">
                    <a href="#events-tracked" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      Events Tracked
                    </a>
                  </li>
                  <li className="my-2">
                    <a href="#setting-up-gtm" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      Setting Up GTM
                    </a>
                  </li>
                  <li className="my-2">
                    <a href="#testing-gtm" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      Testing GTM Integration
                    </a>
                  </li>
                  <li className="my-2">
                    <a href="#ga4-reports" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                      GA4 Reports
                    </a>
                  </li>
                </ul>
              </li>
              <li className="my-2">
                <a href="#opensearch-dashboard" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  OpenSearch Dashboard
                </a>
              </li>
              <li className="my-2">
                <a href="#best-practices" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Best Practices
                </a>
              </li>
              <li className="my-2">
                <a href="#index-statistics" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Index Statistics
                </a>
                <ul className="list-disc pl-6 mt-2 mb-2">
                  <li className="my-2">
                    <a
                      href="#index-statistics-overview"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Overview
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#index-statistics-tabs"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Available Tabs
                    </a>
                  </li>
                  <li className="my-2">
                    <a
                      href="#excel-export"
                      className="text-blue-600 hover:text-blue-800 underline cursor-pointer"
                    >
                      Excel Export
                    </a>
                  </li>
                </ul>
              </li>
              <li className="my-2">
                <a href="#troubleshooting" className="text-blue-600 hover:text-blue-800 underline cursor-pointer">
                  Troubleshooting
                </a>
              </li>
            </ol>
          </nav>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="overview">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">Overview</h2>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The Search Admin App provides a comprehensive interface for managing your OpenSearch configuration and
            search results optimization. This application allows you to fine-tune search weights across different
            content types and create promoted results to enhance user search experiences.
          </p>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="getting-started">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">Getting Started</h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Main Navigation</h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The application has seven main sections accessible via tabs:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4">
            <li>
              <strong className="font-semibold text-zinc-900">Search Weights</strong>: Configure field weights for
              different content indices
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Record Boosting</strong>: Control content type
              prioritization in search rankings
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Vector Search</strong>: Configure semantic search and
              AI-powered embeddings
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Fuzziness Settings</strong>: Control typo tolerance and
              fuzzy matching per index to prevent false positives
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Results Management</strong>: Manage promoted, targeted, and priority
              search results
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Sponsored Content</strong>: Create and manage banner ads
              and modal advertisements with GTM tracking
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">OpenSearch Dashboard</strong>: View the live search
              interface in an iframe
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">User Guide</strong>: Access this comprehensive
              documentation
            </li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="search-weights">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            Search Weights Management
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="search-weights-overview">
            Overview
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Search weights determine the importance of different fields when users search your content. Higher weights
            make fields more influential in search ranking.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="available-content-types">
            Available Content Types
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The system manages weights for multiple content indices:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Member & Society Info</strong> (asmi-wp-pages) - ASM
              organizational information and pages
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Products</strong> (asmi-wp-products) - ASM store products
              and resources
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Education Courses</strong> (asmi-wp-events) - Training and
              educational events
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Industry News</strong> (asmi-wp-asm-news) - Current
              industry updates
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Videos</strong> (asmi-wp-videos) - Educational and
              informational videos
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Books</strong> (asmi-dl-books) - Comprehensive
              reference materials
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Chapters</strong> (asmi-dl-chapters) -
              Specific topical sections
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Images</strong> (asmi-dl-chapter-images) -
              Figures and diagrams from books
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Tables</strong> (asmi-dl-chapter-tables) -
              Data tables from books
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Magazine Articles</strong> (asmi-dl-technical_articles) -
              Technical publications
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">ASM Member News</strong> (asmi-wp-news-articles) -
              Member-focused content
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Conference Papers</strong> (asmi-dl-proceedings) -
              Research presentations
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Alloy Digest Datasheets</strong> (asmi-dl-datasheets) -
              Material specifications
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="configuring-weights">
            Configuring Weights
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Select Content Type</strong>: Click on any content type
              tab to view its field weights
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Adjust Field Weights</strong>: Use the sliders to set
              weights from 0-10 for each field
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Enable/Disable Fields</strong>: Toggle checkboxes to
              include or exclude fields from search
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Add Custom Fields</strong>:
              <ul className="list-disc pl-6 mt-2 mb-2 space-y-2">
                <li>Click "Add Field" button</li>
                <li>Enter the OpenSearch field name</li>
                <li>Set the desired weight</li>
              </ul>
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Save Changes</strong>: Click "Save Configuration" to apply
              your changes
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Reset to Defaults</strong>: Click "Reset to Defaults" to
              restore original settings
            </li>
          </ol>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Common Field Types</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">title</strong>: Document titles (usually high weight: 3-4)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">content</strong>: Main document content (moderate weight:
              1-2)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">description</strong>: Document descriptions (moderate
              weight: 2-3)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">subjects</strong>: Subject tags (low-moderate weight: 1.5)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">keywords</strong>: Keywords and tags (low-moderate weight:
              1.5)
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="weight-color-legend">
            Weight Color Legend
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The field weighting interface uses colors to visually represent the importance level of each field.
            Understanding these colors helps you quickly assess and adjust field priorities:
          </p>

          <div className="my-6 flex flex-col gap-4">
            <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg transition-shadow hover:shadow-md cursor-pointer">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 border-2 border-zinc-900/10 shadow-sm"
                style={{ backgroundColor: '#999999' }}
              ></div>
              <div className="flex-1">
                <strong className="block text-lg text-zinc-900 mb-2 font-semibold">Gray (Weight: 0)</strong>
                <p className="text-sm text-zinc-600 mb-2 leading-relaxed m-0">
                  Field is <strong className="font-semibold text-zinc-900">disabled</strong> and completely excluded
                  from search calculations. No search matches will be found in this field.
                </p>
                <em className="block italic text-sm text-zinc-500 mt-2 font-medium not-italic">
                  Effect: No impact on search results
                </em>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg transition-shadow hover:shadow-md cursor-pointer">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 border-2 border-zinc-900/10 shadow-sm"
                style={{ backgroundColor: '#ff8800' }}
              ></div>
              <div className="flex-1">
                <strong className="block text-lg text-zinc-900 mb-2 font-semibold">Orange (Weight: 0.1 - 1.0)</strong>
                <p className="text-sm text-zinc-600 mb-2 leading-relaxed m-0">
                  Field has <strong className="font-semibold text-zinc-900">low priority</strong> in search rankings.
                  Used for supplementary content that provides context but shouldn't dominate results.
                </p>
                <em className="block italic text-sm text-zinc-500 mt-2 font-medium not-italic">
                  Effect: Minimal boost to search relevance - helps with findability but doesn't prioritize results
                </em>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg transition-shadow hover:shadow-md cursor-pointer">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 border-2 border-zinc-900/10 shadow-sm"
                style={{ backgroundColor: '#888888' }}
              ></div>
              <div className="flex-1">
                <strong className="block text-lg text-zinc-900 mb-2 font-semibold">
                  Dark Gray (Weight: 1.1 - 2.0)
                </strong>
                <p className="text-sm text-zinc-600 mb-2 leading-relaxed m-0">
                  Field has <strong className="font-semibold text-zinc-900">normal priority</strong> in search rankings.
                  This is the baseline weight for most content fields.
                </p>
                <em className="block italic text-sm text-zinc-500 mt-2 font-medium not-italic">
                  Effect: Standard search relevance - balanced contribution to overall document score
                </em>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg transition-shadow hover:shadow-md cursor-pointer">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 border-2 border-zinc-900/10 shadow-sm"
                style={{ backgroundColor: '#44aa44' }}
              ></div>
              <div className="flex-1">
                <strong className="block text-lg text-zinc-900 mb-2 font-semibold">Green (Weight: 2.1 - 4.0)</strong>
                <p className="text-sm text-zinc-600 mb-2 leading-relaxed m-0">
                  Field has <strong className="font-semibold text-zinc-900">high priority</strong> in search rankings.
                  Documents matching search terms in these fields will rank significantly higher.
                </p>
                <em className="block italic text-sm text-zinc-500 mt-2 font-medium not-italic">
                  Effect: Strong boost to search relevance - makes documents more likely to appear in top results
                </em>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg transition-shadow hover:shadow-md cursor-pointer">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 border-2 border-zinc-900/10 shadow-sm"
                style={{ backgroundColor: '#2266dd' }}
              ></div>
              <div className="flex-1">
                <strong className="block text-lg text-zinc-900 mb-2 font-semibold">Blue (Weight: 4.1 - 6.0)</strong>
                <p className="text-sm text-zinc-600 mb-2 leading-relaxed m-0">
                  Field has <strong className="font-semibold text-zinc-900">very high priority</strong> in search
                  rankings. Typically used for titles and other critical identifying content.
                </p>
                <em className="block italic text-sm text-zinc-500 mt-2 font-medium not-italic">
                  Effect: Major boost to search relevance - documents matching here will dominate search results
                </em>
              </div>
            </div>

            <div className="flex items-start gap-4 p-4 bg-zinc-50 border border-zinc-200 rounded-lg transition-shadow hover:shadow-md cursor-pointer">
              <div
                className="w-5 h-5 rounded-full flex-shrink-0 mt-0.5 border-2 border-zinc-900/10 shadow-sm"
                style={{ backgroundColor: '#8844dd' }}
              ></div>
              <div className="flex-1">
                <strong className="block text-lg text-zinc-900 mb-2 font-semibold">Purple (Weight: 6.1+)</strong>
                <p className="text-sm text-zinc-600 mb-2 leading-relaxed m-0">
                  Field has <strong className="font-semibold text-zinc-900">maximum priority</strong> in search
                  rankings. Reserved for the most critical searchable content.
                </p>
                <em className="block italic text-sm text-zinc-500 mt-2 font-medium not-italic">
                  Effect: Maximum boost to search relevance - documents matching here will almost always appear first
                </em>
              </div>
            </div>
          </div>

          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-400 rounded-lg p-6 my-8">
            <h4 className="text-amber-700 text-lg font-semibold mb-4 m-0">How Colors Affect Search Results</h4>
            <ul className="list-disc pl-6 my-2 space-y-1.5">
              <li className="text-sm text-amber-900">
                <strong className="font-semibold text-zinc-900">User searches for "corrosion resistance"</strong>
              </li>
              <li className="text-sm text-amber-900">
                <strong className="font-semibold text-zinc-900">Purple Title Match</strong>: Document with "corrosion
                resistance" in title (weight 6+) scores very high
              </li>
              <li className="text-sm text-amber-900">
                <strong className="font-semibold text-zinc-900">Green Description Match</strong>: Document with
                "corrosion resistance" in description (weight 3) scores moderately high
              </li>
              <li className="text-sm text-amber-900">
                <strong className="font-semibold text-zinc-900">Orange Content Match</strong>: Document with "corrosion
                resistance" in content (weight 1) scores lower
              </li>
              <li className="text-sm text-amber-900">
                <strong className="font-semibold text-zinc-900">Gray Keywords Match</strong>: Document with "corrosion
                resistance" in disabled keywords field (weight 0) scores nothing for this field
              </li>
            </ul>
            <p className="mt-4 m-0 p-3 bg-amber-50 rounded border-l-4 border-amber-500 text-amber-900 font-medium">
              <strong className="font-semibold text-zinc-900">Result</strong>: Purple title matches appear first,
              followed by green description matches, then orange content matches. Gray disabled fields contribute
              nothing to the ranking.
            </p>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="search-weights-import-export">
            Import/Export JSON Configuration
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Field weight configurations can be exported and imported as JSON files for backup, sharing between
            environments, or bulk editing of multiple index weights at once.
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Exporting Weights</h4>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Navigate to the Search Weights tab and click "Edit All Weights"</li>
            <li>Click the "Export JSON" button in the header</li>
            <li>A JSON file will be downloaded containing all field weights for all content types</li>
            <li>Use this file for backups or to transfer settings to another environment</li>
          </ol>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Importing Weights</h4>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Navigate to the Search Weights tab and click "Edit All Weights"</li>
            <li>Click the "Import JSON" button in the header</li>
            <li>Paste your JSON configuration into the text area</li>
            <li>Click "Import" to apply the configuration</li>
            <li>Review the changes and click "Save All Changes" to persist them</li>
          </ol>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">JSON Format</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">The JSON file must follow this structure:</p>
          <pre className="bg-zinc-900 text-zinc-100 p-4 rounded-lg overflow-x-auto text-sm mb-4">
            {`{
  "indices": [
    {
      "indexName": "asmi-dl-chapters",
      "weights": [
        { "field": "title", "weight": 5, "enabled": true },
        { "field": "content", "weight": 1, "enabled": true },
        { "field": "keywords", "weight": 3, "enabled": true }
      ]
    },
    {
      "indexName": "asmi-dl-datasheets",
      "weights": [
        { "field": "title", "weight": 4, "enabled": true },
        { "field": "description", "weight": 2, "enabled": true }
      ]
    }
  ]
}`}
          </pre>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">indices</strong>: Array of index configurations (required)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">indexName</strong>: The OpenSearch index name (required)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">weights</strong>: Array of field weight objects (required)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">field</strong>: The field name (e.g., "title", "content")
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">weight</strong>: Weight value between 0 and 10
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">enabled</strong>: Whether the field is included in search
              (optional, defaults to true)
            </li>
          </ul>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-4">
            <h4 className="text-base font-semibold text-blue-900 mb-2">Tip: Bulk Editing</h4>
            <p className="text-blue-800 text-sm">
              Export your current configuration, edit the JSON file in a text editor to make bulk changes across
              multiple content types, then import the modified file. This is much faster than adjusting each field
              individually through the UI.
            </p>
          </div>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="record-boosting">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            Record Boosting Management
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="record-boosting-overview">
            Overview
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Record boosting is a powerful feature that allows you to control how different content types are prioritized
            in search results. Unlike field weights (which affect individual fields within documents), record boosting
            multiplies the entire relevance score of documents from specific indices, making certain types of content
            consistently rank higher or lower.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="how-record-boosting-works">
            How Record Boosting Works
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Record boosting works in conjunction with field weighting to create sophisticated search relevance ranking:
          </p>

          <div className="bg-gradient-to-br from-cyan-50 to-teal-50 border-2 border-cyan-400 rounded-lg p-6 my-6 text-center">
            <h4 className="text-cyan-700 text-lg font-semibold mb-2 m-0">Search Scoring Formula</h4>
            <p className="text-base font-mono text-cyan-900 m-0">
              <strong className="font-semibold">Final Score = (Field Weight × Query Match Score) × Record Boost</strong>
            </p>
          </div>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Step-by-Step Calculation</h4>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Field Weight Calculation</strong>: Each field gets a
              weight (e.g., title^3, content^1, keywords^2)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Query Match Score</strong>: OpenSearch calculates how well
              the search query matches the field content (0.0-1.0)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Field Score</strong>: Field Weight × Query Match Score
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Document Score</strong>: Sum of all field scores for the
              document
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Final Score</strong>: Document Score × Record Boost for
              that index
            </li>
          </ol>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Practical Example</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Consider a search for "stainless steel corrosion" across different content types:
          </p>

          <div className="bg-zinc-50 border border-zinc-300 border-l-4 border-l-orange-500 rounded p-6 my-6">
            <h5 className="text-orange-700 text-base font-semibold mb-4 m-0">Document A: Chapter from Digital Book</h5>
            <ul className="list-none pl-0 space-y-1 mb-2">
              <li className="font-mono text-sm text-zinc-700">Title match: 3 (weight) × 0.8 (relevance) = 2.4</li>
              <li className="font-mono text-sm text-zinc-700">Content match: 1 (weight) × 0.6 (relevance) = 0.6</li>
              <li className="font-mono text-sm text-zinc-700">Keywords match: 2 (weight) × 0.9 (relevance) = 1.8</li>
              <li className="font-mono text-sm text-zinc-700">
                <strong className="font-semibold text-zinc-900">Document Score: 2.4 + 0.6 + 1.8 = 4.8</strong>
              </li>
              <li className="font-mono text-sm text-zinc-700">
                <strong className="font-semibold text-zinc-900">Final Score: 4.8 × 1.0 (book boost) = 4.8</strong>
              </li>
            </ul>

            <h5 className="text-orange-700 text-base font-semibold mb-4 mt-4 m-0">Document B: Alloy Datasheet</h5>
            <ul className="list-none pl-0 space-y-1 mb-2">
              <li className="font-mono text-sm text-zinc-700">Title match: 3 (weight) × 0.7 (relevance) = 2.1</li>
              <li className="font-mono text-sm text-zinc-700">Content match: 1 (weight) × 0.5 (relevance) = 0.5</li>
              <li className="font-mono text-sm text-zinc-700">Keywords match: 2 (weight) × 0.8 (relevance) = 1.6</li>
              <li className="font-mono text-sm text-zinc-700">
                <strong className="font-semibold text-zinc-900">Document Score: 2.1 + 0.5 + 1.6 = 4.2</strong>
              </li>
              <li className="font-mono text-sm text-zinc-700">
                <strong className="font-semibold text-zinc-900">Final Score: 4.2 × 2.0 (datasheet boost) = 8.4</strong>
              </li>
            </ul>

            <p className="mt-4 m-0 p-3 bg-orange-50 rounded border-l-3 border-l-4 border-orange-500">
              <strong className="font-semibold text-zinc-900">Result:</strong> The datasheet (8.4) ranks higher than the
              book chapter (4.8) despite having a lower document score, because datasheets have a 2.0× boost.
            </p>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Available Content Types</h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            You can configure record boosting for all content indices in the system:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Member & Society Info</strong> (asmi-wp-pages) - ASM
              organizational information and pages
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Products</strong> (asmi-wp-products) - ASM store products
              and resources
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Education Courses</strong> (asmi-wp-events) - Training and
              educational events
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Industry News</strong> (asmi-wp-asm-news) - Current
              industry updates
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Videos</strong> (asmi-wp-videos) - Educational and
              informational videos
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Books</strong> (asmi-dl-books) - Comprehensive
              reference materials
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Chapters</strong> (asmi-dl-chapters) -
              Specific topical sections
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Images</strong> (asmi-dl-chapter-images) -
              Figures and diagrams from books
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Tables</strong> (asmi-dl-chapter-tables) -
              Data tables from books
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Magazine Articles</strong> (asmi-dl-technical_articles) -
              Technical publications
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">ASM Member News</strong> (asmi-wp-news-articles) -
              Member-focused content
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Conference Papers</strong> (asmi-dl-proceedings) -
              Research presentations
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Alloy Digest Datasheets</strong> (asmi-dl-datasheets) -
              Material specifications
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="configuring-record-boosting">
            Configuring Record Boosting
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Navigate to Record Boosting Tab</strong>: Click the
              "Record Boosting" tab in the main navigation
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Set Default Boost</strong>: Configure the baseline boost
              value (0.1-3.0) for indices not explicitly configured
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Configure Index-Specific Boosts</strong>:
              <ul className="list-disc pl-6 mt-2 mb-2 space-y-2">
                <li>Use the sliders to adjust boost values from 0.1 to 3.0 for each content type</li>
                <li>Enable/disable specific indices with the toggle switches</li>
                <li>Disabled indices will not appear in search results</li>
              </ul>
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Preview Impact</strong>: Review the "Boost Impact Preview"
              to see which content types will rank highest
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Save Configuration</strong>: Click "Save Configuration" to
              apply your changes
            </li>
          </ol>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="boost-value-guidelines">
            Boost Value Guidelines
          </h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">0.1-0.4 (Very Low)</strong>: Significantly de-prioritize
              content (troubleshooting or low-quality content)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">0.5-0.9 (Low)</strong>: Slightly reduce priority
              (background or supplementary content)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">1.0 (Baseline)</strong>: Normal priority - no boost
              applied
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">1.1-1.5 (High)</strong>: Moderately prioritize (important
              or popular content)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">1.6-2.0 (Very High)</strong>: Strongly prioritize (premium
              or critical content)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">2.1-3.0 (Maximum)</strong>: Extremely prioritize
              (emergency content or featured materials)
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="strategic-use-cases">
            Strategic Use Cases
          </h3>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Scenario 1: Technical Reference Priority</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Prioritize technical datasheets and specifications for engineering searches:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Alloy Digest Datasheets: 2.0× boost</li>
            <li>Conference Papers: 1.5× boost</li>
            <li>Digital Books: 1.2× boost</li>
            <li>News Articles: 0.8× boost</li>
          </ul>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Scenario 2: Educational Content Focus</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Emphasize learning materials for educational users:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Digital Book Chapters: 1.8× boost</li>
            <li>ASM Education Events: 1.6× boost</li>
            <li>Digital Books: 1.4× boost</li>
            <li>Industry News: 0.7× boost</li>
          </ul>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Scenario 3: Current Information Priority</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">Favor recent and timely content:</p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Industry News: 1.7× boost</li>
            <li>ASM Member News: 1.5× boost</li>
            <li>Conference Papers: 1.3× boost</li>
            <li>Digital Books: 0.9× boost</li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Interaction with Other Features</h3>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Record Boosting + Field Weights</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Record boosting and field weights work together multiplicatively. Consider this when configuring both:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>High field weights + high record boost = extremely high scores</li>
            <li>Low field weights + high record boost = moderate scores</li>
            <li>High field weights + low record boost = suppressed scores</li>
          </ul>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Record Boosting + Results Management</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Sponsored and Promoted results always appear in the promoted section above organic results. Priority results appear at the top of organic results. Use record boosting to influence the ranking of other organic results.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Monitoring and Optimization</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Test with Representative Queries</strong>: Use common
              search terms to verify ranking changes
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Monitor User Behavior</strong>: Track click-through rates
              on different content types
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Gradual Adjustments</strong>: Make small incremental
              changes rather than dramatic shifts
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Document Changes</strong>: Keep notes on why you made
              specific boost adjustments
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Regular Review</strong>: Periodically review boost
              settings as content and user needs evolve
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="record-boosting-import-export">
            Import/Export JSON Configuration
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Record boosting configurations can be exported and imported as JSON files for backup, sharing between
            environments, or bulk editing.
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Exporting Configuration</h4>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Click the "Export JSON" button in the Record Boosting tab header</li>
            <li>A JSON file will be downloaded containing all index boost values and settings</li>
            <li>Use this file for backups or to transfer settings to another environment</li>
          </ol>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Importing Configuration</h4>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Click the "Import JSON" button in the Record Boosting tab header</li>
            <li>Paste your JSON configuration into the text area</li>
            <li>Click "Import" to apply the configuration</li>
            <li>Review the changes and click "Save Configuration" to persist them</li>
          </ol>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">JSON Format</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">The JSON file must follow this structure:</p>
          <pre className="bg-zinc-900 text-zinc-100 p-4 rounded-lg overflow-x-auto text-sm mb-4">
            {`{
  "indices": [
    {
      "indexName": "asmi-dl-chapters",
      "boost": 1.2,
      "enabled": true
    },
    {
      "indexName": "asmi-dl-datasheets",
      "boost": 2.0,
      "enabled": true
    }
  ],
  "defaultBoost": 1.0,
  "lastUpdated": "2025-01-15T10:30:00.000Z"
}`}
          </pre>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">indices</strong>: Array of index configurations (required)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">indexName</strong>: The OpenSearch index name (required)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">boost</strong>: Boost multiplier value between 0.1 and 3.0
              (required)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">enabled</strong>: Whether the index is included in search
              (optional, defaults to true)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">defaultBoost</strong>: Default boost for unlisted indices
              (optional)
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Troubleshooting Record Boosting</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Changes not taking effect</strong>: Ensure you saved the
              configuration and wait for cache refresh
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Unexpected ranking changes</strong>: Check if field
              weights were also modified recently
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Content not appearing</strong>: Verify the index is
              enabled and boost value is above 0.1
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Performance issues</strong>: Very high boost values
              (&gt;2.0) may affect search performance
            </li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="fuzziness-settings">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            Fuzziness Settings
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="fuzziness-overview">
            Overview
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Fuzziness settings control how tolerant the search engine is of spelling mistakes and typos. This feature
            allows you to fine-tune the balance between finding relevant results with slight misspellings and avoiding
            false matches where similar words are confused for each other.
          </p>

          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border-2 border-amber-400 rounded-lg p-6 my-6">
            <h4 className="text-amber-700 text-lg font-semibold mb-3 m-0">Real-World Example</h4>
            <p className="text-sm text-amber-900 mb-2">
              A user searching for author <strong>"Ed Cole"</strong> might receive results about{' '}
              <strong>"Cold Spray"</strong> because "Cole" is similar to "Cold" when fuzzy matching is too lenient.
            </p>
            <p className="text-sm text-amber-900">
              By adjusting fuzziness settings per index, you can reduce these false matches while still allowing for
              legitimate typo corrections like "aluminium" matching "aluminum".
            </p>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="what-is-fuzziness">
            What is Fuzziness?
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Fuzziness is measured in "edit distance" - the number of single-character changes (insertions, deletions,
            substitutions, or transpositions) needed to transform one word into another.
          </p>

          <div className="overflow-x-auto mb-6">
            <table className="min-w-full border border-zinc-200 rounded-lg overflow-hidden">
              <thead className="bg-zinc-100">
                <tr>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-zinc-900 border-b border-zinc-200">
                    Search Term
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-zinc-900 border-b border-zinc-200">
                    Matches (Edit Distance)
                  </th>
                  <th className="px-4 py-3 text-left text-sm font-semibold text-zinc-900 border-b border-zinc-200">
                    Distance
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr className="bg-white">
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">steel</td>
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">steel, steal</td>
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">0, 1</td>
                </tr>
                <tr className="bg-zinc-50">
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">Cole</td>
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">Cole, Cold, Coil</td>
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">0, 1, 1</td>
                </tr>
                <tr className="bg-white">
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">aluminium</td>
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">aluminium, aluminum</td>
                  <td className="px-4 py-3 text-sm text-zinc-700 border-b border-zinc-100">0, 1</td>
                </tr>
              </tbody>
            </table>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="fuzziness-presets">
            Available Presets
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The system provides several presets for different use cases:
          </p>

          <div className="space-y-4 mb-6">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <h4 className="text-blue-800 font-semibold mb-2">AUTO (Default)</h4>
              <p className="text-sm text-blue-700 mb-2">
                Dynamically adjusts based on term length:
              </p>
              <ul className="list-disc pl-6 text-sm text-blue-700">
                <li>1-2 character terms: No fuzzy matching (exact match only)</li>
                <li>3-5 character terms: 1 edit allowed</li>
                <li>6+ character terms: 2 edits allowed</li>
              </ul>
            </div>

            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <h4 className="text-green-800 font-semibold mb-2">Strict (AUTO:5,8)</h4>
              <p className="text-sm text-green-700 mb-2">
                More restrictive fuzzy matching, recommended for indexes where precision matters:
              </p>
              <ul className="list-disc pl-6 text-sm text-green-700">
                <li>1-4 character terms: No fuzzy matching</li>
                <li>5-7 character terms: 1 edit allowed</li>
                <li>8+ character terms: 2 edits allowed</li>
              </ul>
              <p className="text-sm text-green-700 mt-2 italic">
                Best for: Author names, precise technical terms, short codes
              </p>
            </div>

            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <h4 className="text-yellow-800 font-semibold mb-2">Moderate (AUTO:4,7)</h4>
              <p className="text-sm text-yellow-700 mb-2">
                Balanced approach between precision and recall:
              </p>
              <ul className="list-disc pl-6 text-sm text-yellow-700">
                <li>1-3 character terms: No fuzzy matching</li>
                <li>4-6 character terms: 1 edit allowed</li>
                <li>7+ character terms: 2 edits allowed</li>
              </ul>
            </div>

            <div className="bg-zinc-100 border border-zinc-300 rounded-lg p-4">
              <h4 className="text-zinc-800 font-semibold mb-2">Fixed Values (0, 1, 2)</h4>
              <p className="text-sm text-zinc-700">
                Fixed edit distance regardless of term length:
              </p>
              <ul className="list-disc pl-6 text-sm text-zinc-700 mt-2">
                <li><strong>0</strong>: Exact matches only - no typo tolerance</li>
                <li><strong>1</strong>: Allow 1 character difference</li>
                <li><strong>2</strong>: Allow up to 2 character differences</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="configuring-fuzziness">
            Configuring Fuzziness
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            To configure fuzziness settings:
          </p>

          <ol className="list-decimal pl-6 space-y-4 text-zinc-700 mb-6">
            <li>
              <strong className="font-semibold text-zinc-900">Navigate to Fuzziness Settings</strong>: Click
              "Fuzziness Settings" in the sidebar navigation.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Set Global Default</strong>: Choose a default fuzziness
              value that applies to all indexes without custom settings. AUTO is recommended for most cases.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Add Index Overrides</strong>: Click "Add Index" to create
              a custom fuzziness setting for specific indexes that need different behavior.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Enable/Disable Overrides</strong>: Use the toggle to
              enable or disable individual index overrides without deleting them.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Save Configuration</strong>: Click "Save Configuration"
              to apply your changes.
            </li>
          </ol>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="fuzziness-best-practices">
            Best Practices
          </h3>

          <div className="bg-gradient-to-br from-emerald-50 to-teal-50 border-2 border-emerald-400 rounded-lg p-6 my-6">
            <h4 className="text-emerald-700 text-lg font-semibold mb-3 m-0">Recommendations</h4>
            <ul className="list-disc pl-6 space-y-2">
              <li className="text-sm text-emerald-900">
                <strong>Start with AUTO</strong>: The default AUTO setting works well for most content types.
              </li>
              <li className="text-sm text-emerald-900">
                <strong>Use Strict for names</strong>: For indexes with author names or proper nouns (like ASM Member
                News), consider using the Strict preset (AUTO:5,8) to avoid matching "Cole" with "Cold".
              </li>
              <li className="text-sm text-emerald-900">
                <strong>Test before deploying</strong>: After changing fuzziness settings, test searches that previously
                had false positives to verify the issue is resolved.
              </li>
              <li className="text-sm text-emerald-900">
                <strong>Consider the content type</strong>: Technical content with precise terminology may benefit from
                stricter settings, while general content may need more flexibility.
              </li>
              <li className="text-sm text-emerald-900">
                <strong>Use 0 sparingly</strong>: Setting fuzziness to 0 (exact match only) eliminates all typo
                tolerance, which may frustrate users who make legitimate spelling mistakes.
              </li>
            </ul>
          </div>

          <h4 className="text-lg font-semibold text-zinc-900 mb-3 mt-6">Troubleshooting</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4">
            <li>
              <strong className="font-semibold text-zinc-900">False positives (wrong matches)</strong>: Lower the
              fuzziness value or use the Strict preset for the affected index.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Missing expected results</strong>: Increase the fuzziness
              value to allow more typo tolerance.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Changes not taking effect</strong>: Click the "Refresh
              Cache" button after saving to ensure the search worker uses the new settings.
            </li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="search-modes">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            Search Modes Configuration
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="search-modes-overview">
            Overview
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Search Modes allow you to create different search experiences for different user needs. The system supports
            two distinct search modes: <strong className="font-semibold text-zinc-900">General Search</strong> and{' '}
            <strong className="font-semibold text-zinc-900">Research Mode</strong>. Each mode can be configured to
            search different content indexes, providing tailored results for different use cases.
          </p>

          <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border-2 border-blue-400 rounded-lg p-6 my-6">
            <h4 className="text-blue-700 text-lg font-semibold mb-3 m-0">Key Benefits</h4>
            <ul className="list-disc pl-6 my-2 space-y-1.5">
              <li className="text-sm text-blue-900">
                <strong className="font-semibold text-zinc-900">Tailored Search Experiences</strong>: Different users
                have different needs - researchers need technical depth while general users need breadth
              </li>
              <li className="text-sm text-blue-900">
                <strong className="font-semibold text-zinc-900">Cleaner Results</strong>: Hide irrelevant content types
                to reduce noise and improve findability
              </li>
              <li className="text-sm text-blue-900">
                <strong className="font-semibold text-zinc-900">Better Performance</strong>: Searching fewer indexes
                improves response times
              </li>
              <li className="text-sm text-blue-900">
                <strong className="font-semibold text-zinc-900">User Control</strong>: Users can switch modes with a
                simple toggle on the search page
              </li>
            </ul>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="general-search-mode">
            General Search Mode
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            General Search Mode is designed for broad discovery across all ASM content. This is the default mode that
            most users will use for everyday searches.
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">
            Typical Content Types (Default Configuration)
          </h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">ASM Information</strong> - General member and society
              information
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">ASM Store</strong> - Products and resources available for
              purchase
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">ASM Education</strong> - Training courses and educational
              events
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Industry News</strong> - Current updates and news articles
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Videos</strong> - Educational and informational video
              content
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Books</strong> - Complete reference materials
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Magazine Articles</strong> - Technical publication
              articles
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">ASM Member News</strong> - Member-focused content and
              announcements
            </li>
          </ul>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Use Cases</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Members looking for general information about ASM services</li>
            <li>Users shopping for products, courses, or events</li>
            <li>Professionals staying current with industry news</li>
            <li>General topic exploration and discovery</li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="research-mode">
            Research Mode
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Research Mode is optimized for deep technical research and scholarly work. It focuses on technical content
            while excluding commercial and general information.
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">
            Typical Content Types (Default Configuration)
          </h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Digital Books</strong> - Comprehensive technical
              references
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Chapters</strong> - Specific technical
              sections and topics
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Images</strong> - Technical diagrams,
              figures, and illustrations
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Digital Book Tables</strong> - Data tables and
              specifications
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Magazine Articles</strong> - Peer-reviewed technical
              articles
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Conference Papers</strong> - Research presentations and
              proceedings
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Alloy Digest Datasheets</strong> - Material specifications
              and properties
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Materials Database</strong> - Comprehensive materials data
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Micrograph Data</strong> - Metallographic images and
              analysis
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Phase Diagrams</strong> - Alloy phase transformation data
            </li>
          </ul>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Use Cases</h4>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Engineers researching material properties and specifications</li>
            <li>Scientists conducting literature reviews</li>
            <li>Students working on technical projects or papers</li>
            <li>Researchers looking for specific data, charts, or technical details</li>
            <li>Materials scientists comparing alloy compositions</li>
          </ul>

          <div className="bg-gradient-to-br from-amber-50 to-yellow-50 border-2 border-amber-400 rounded-lg p-6 my-6">
            <h4 className="text-amber-700 text-lg font-semibold mb-3 m-0">Example: How Modes Affect Search Results</h4>
            <p className="text-sm text-amber-900 mb-3">
              <strong className="font-semibold text-zinc-900">Search Query:</strong> "stainless steel corrosion"
            </p>

            <div className="mb-4">
              <h5 className="text-orange-700 text-base font-semibold mb-2 m-0">General Search Mode Results:</h5>
              <ul className="list-disc pl-6 my-2 space-y-1">
                <li className="text-sm text-amber-900">
                  Training course: "Understanding Corrosion in Stainless Steel"
                </li>
                <li className="text-sm text-amber-900">Book: "ASM Handbook - Corrosion Resistance"</li>
                <li className="text-sm text-amber-900">Product: Stainless steel reference materials</li>
                <li className="text-sm text-amber-900">News article: Latest developments in corrosion prevention</li>
              </ul>
            </div>

            <div>
              <h5 className="text-orange-700 text-base font-semibold mb-2 m-0">Research Mode Results:</h5>
              <ul className="list-disc pl-6 my-2 space-y-1">
                <li className="text-sm text-amber-900">Datasheet: 316L Stainless Steel corrosion data</li>
                <li className="text-sm text-amber-900">Book chapter: Mechanisms of pitting corrosion</li>
                <li className="text-sm text-amber-900">Conference paper: Electrochemical testing methods</li>
                <li className="text-sm text-amber-900">Table: Corrosion rates in various environments</li>
                <li className="text-sm text-amber-900">Micrograph: Intergranular corrosion examples</li>
              </ul>
            </div>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="configuring-search-modes">
            Configuring Search Modes
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            You can customize which content indexes are included in each search mode to match your organization's needs.
          </p>

          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Navigate to Search Modes Tab</strong>: Click the "Search
              Modes" option in the main navigation
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Select Mode to Configure</strong>: Choose between "General
              Search" or "Research Mode" using the toggle buttons
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Enable/Disable Content Types</strong>:
              <ul className="list-disc pl-6 mt-2 mb-2 space-y-2">
                <li>Check boxes to include content types in the selected mode</li>
                <li>Uncheck boxes to exclude content types from the mode</li>
                <li>Changes affect only the currently selected mode</li>
              </ul>
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Review Configuration</strong>: The interface shows which
              indexes are enabled for each mode with a visual indicator
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Save Changes</strong>: Click "Save Configuration" to apply
              your changes to the live search
            </li>
          </ol>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Important Behaviors</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Facet Filter Auto-Clear</strong>: When users toggle
              between modes, any active facet filters for content types not available in the new mode are automatically
              cleared
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Independent Configurations</strong>: Changes to General
              Search do not affect Research Mode settings and vice versa
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">User Preference Persistence</strong>: When users switch
              modes, their preference is remembered for their current session
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Real-Time Updates</strong>: Mode configuration changes
              take effect immediately after saving
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Best Practices</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Keep General Search Broad</strong>: Include diverse
              content types to serve varied user needs
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Keep Research Mode Focused</strong>: Limit to technical
              and scholarly content for cleaner, more relevant results
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Test Both Modes</strong>: Use the OpenSearch Dashboard to
              verify search results in both modes after making changes
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Consider Your Audience</strong>: Align mode configurations
              with your users' primary use cases
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Document Rationale</strong>: Keep notes on why certain
              content types are included/excluded in each mode
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Gather Feedback</strong>: Monitor how users interact with
              each mode and adjust based on usage patterns
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Interaction with Other Features</h3>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Search Modes + Record Boosting</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Record boosting configurations can be mode-specific. You can set different boost values for the same content
            type in General Search vs Research Mode to further optimize relevance for each context.
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Search Modes + Field Weights</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Field weight configurations apply to all modes. However, if a content type is disabled in a mode, its field
            weights won't affect results in that mode.
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Search Modes + Facets</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The facet panel automatically adjusts to show only content types available in the current mode. When users
            switch modes, incompatible facet selections are automatically cleared.
          </p>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="promoted-results">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            Results Management
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="promoted-results-overview">
            Overview
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Results Management allows you to feature specific content in search results for targeted keywords,
            improving content discoverability and user engagement. There are three types of managed results.
          </p>

          {/* Important callout box */}
          <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
            <h4 className="text-base font-semibold text-amber-900 mb-2">Result Type Behavior</h4>
            <ul className="list-disc list-inside space-y-2 text-amber-800 text-sm">
              <li>
                <strong>Sponsored & Promoted:</strong> Both appear in the promoted section at the top. <strong>Combined maximum of 3</strong> (e.g., 2 Sponsored + 1 Promoted = 3 total). Sponsored shows a prominent blue badge; Promoted shows a subtle amber badge.
              </li>
              <li>
                <strong>Priority:</strong> Appears as the first regular search results (no badge, no special styling). <strong>No limit</strong> on how many can appear. Requires selecting a Force Result.
              </li>
            </ul>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="keyword-matching">
            Keyword Matching Behavior
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Keywords determine when a promoted result appears. The system supports two matching modes:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Standard Keywords:</strong> Match if the keyword appears
              anywhere in the search query, or if the query appears in the keyword.
              <br />
              <span className="text-zinc-500 text-sm ml-6">
                Example: Keyword <code className="bg-zinc-100 px-1 rounded">failure analysis</code> matches searches for
                "failure analysis training", "component failure analysis", etc.
              </span>
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Exact Phrase Keywords (Quoted):</strong> Wrap a keyword in
              double quotes to require an exact phrase match.
              <br />
              <span className="text-zinc-500 text-sm ml-6">
                Example: Keyword <code className="bg-zinc-100 px-1 rounded">"component failure analysis course"</code>{' '}
                will <em>only</em> match when the user searches for that exact phrase, not just "failure analysis".
              </span>
            </li>
          </ul>
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
            <h4 className="text-base font-semibold text-blue-900 mb-2">Tip: Reducing Unwanted Matches</h4>
            <p className="text-blue-800 text-sm">
              If a promoted result appears too frequently (e.g., for broad terms like "failure analysis"), use quoted
              exact phrases for more specific targeting. This prevents the result from appearing on every search that
              contains those common words.
            </p>
          </div>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Types of Managed Results</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Sponsored</strong>: Appears in the promoted section with a prominent "SPONSORED" badge. Best for paid or featured content. Max 3 combined with Promoted.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Promoted</strong>: Appears in the promoted section with a subtle "Promoted" badge. Best for strategic content placement without appearing overly promotional. Max 3 combined with Sponsored.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Priority</strong>: Appears as the first regular search results with no special styling. Best for ensuring specific content appears at the top without any promotional appearance. No limit. Requires selecting a Force Result.
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="creating-promoted-results">
            Creating Managed Results
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              Navigate to the <strong className="font-semibold text-zinc-900">Results Management</strong> tab
            </li>
            <li>
              Click <strong className="font-semibold text-zinc-900">"Add Managed Result"</strong>
            </li>
            <li>
              Fill in the form:
              <ul className="list-disc pl-6 mt-2 mb-2 space-y-2">
                <li>
                  <strong className="font-semibold text-zinc-900">Category</strong>: Choose Sponsored, Promoted, or Priority
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Keywords</strong>: Enter search terms that should
                  trigger this result (comma-separated)
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Title</strong>: The headline that will appear in
                  search results (not used for Priority with Force Result)
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Description</strong>: Brief description of the content
                  (not used for Priority with Force Result)
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">URL</strong>: Link destination (optional for forced
                  results)
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Force Result</strong>: Search and select an existing document to display (required for Priority)
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Priority</strong>: Controls display order. For Sponsored/Promoted: higher numbers appear first (1-10). For Priority: lower numbers appear first (1, 2, 3...).
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Date Range</strong>: Set start and end dates for when this result should appear
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Status</strong>: Enable/disable the managed result
                </li>
              </ul>
            </li>
            <li>
              Click <strong className="font-semibold text-zinc-900">"Create"</strong> to save the managed result
            </li>
          </ol>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="force-result-feature">
            Force Result Feature
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            You can force a specific search result to appear as a promoted result:
          </p>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>In the promoted result form, use the search box to find existing content</li>
            <li>Select a result from the dropdown</li>
            <li>This will automatically populate the title and create a direct link to that content</li>
          </ol>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Managing Existing Results</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Edit</strong>: Click the pencil icon on any result card
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Delete</strong>: Click the trash icon and confirm deletion
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">View Status</strong>: Active results show in full color,
              inactive ones are grayed out
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="bulk-operations">
            Bulk Operations
          </h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Import CSV</strong>: Upload a CSV file with multiple
              promoted results
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Export CSV</strong>: Download all promoted results as a
              CSV file for backup or editing
            </li>
          </ul>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">CSV Format</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">CSV files should include these columns:</p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Keywords (pipe-separated: keyword1|keyword2)</li>
            <li>Title</li>
            <li>Description</li>
            <li>URL</li>
            <li>Category (sponsored/targeted)</li>
            <li>Start Date (YYYY-MM-DD)</li>
            <li>End Date (YYYY-MM-DD)</li>
            <li>Priority (1-10)</li>
            <li>Active (true/false)</li>
            <li>Force Result (JSON object, optional)</li>
            <li>Created At (ISO date)</li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="advertisement-management">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            Advertisement Management
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="advertisement-overview">
            Overview
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The Advertisements section allows you to create and manage banner ads and modal popups that appear in the
            search interface. Ads support targeting by keywords and content types, UTM tracking, and scheduling.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="advertisement-types">
            Advertisement Types
          </h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Banner Ads</strong>: Display in sidebars or top/bottom of
              search results
              <ul className="list-disc pl-6 mt-2 mb-2 space-y-2">
                <li>Left Sidebar: 300x600px</li>
                <li>Right Sidebar: 300x600px</li>
                <li>Top/Bottom: 728x90px</li>
              </ul>
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Modal Popups</strong>: Overlay ads that appear over
              content (600x400px recommended)
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="creating-advertisement">
            Creating an Advertisement
          </h3>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              Navigate to the <strong className="font-semibold text-zinc-900">Advertisements</strong> tab
            </li>
            <li>Click the "+ Add Advertisement" button</li>
            <li>
              Fill in the required information:
              <ul className="list-disc pl-6 mt-2 mb-2 space-y-2">
                <li>
                  <strong className="font-semibold text-zinc-900">Internal Name</strong>: A descriptive name for
                  management (not shown to users)
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Type</strong>: Choose Banner or Modal
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Placement</strong>: Select where the banner appears
                  (for banner ads only)
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Image URL</strong>: Link to your ad creative
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Click URL</strong>: Where users go when they click the
                  ad
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">UTM Parameters</strong>: Track ad performance in
                  analytics (optional)
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Targeting</strong>: Show on all searches, or target
                  specific keywords/content types
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Schedule</strong>: Start and end dates for the
                  campaign
                </li>
                <li>
                  <strong className="font-semibold text-zinc-900">Priority</strong>: Higher numbers show first when
                  multiple ads match (1-10)
                </li>
              </ul>
            </li>
            <li>Click "Create Advertisement"</li>
          </ol>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="utm-tracking">
            UTM Tracking
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Use UTM parameters to track ad performance in Google Analytics or other tools:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">utm_source</strong>: Identifies where the click came from
              (e.g., "asm_search")
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_medium</strong>: The marketing medium (e.g.,
              "banner_ad", "modal")
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_campaign</strong>: The specific campaign name (e.g.,
              "q1_steel_promo")
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_content</strong>: Differentiates similar content
              (e.g., "sidebar_300x600")
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_term</strong>: Identifies paid search keywords
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Managing Advertisements</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">View All Ads</strong>: The Advertisements tab shows all
              ads with preview images
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Edit</strong>: Click the "Edit" button on any ad card to
              modify settings
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Activate/Deactivate</strong>: Toggle ads on or off without
              deleting them
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Delete</strong>: Permanently remove an advertisement
            </li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="gtm-integration">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            Google Tag Manager (GTM) Integration
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="gtm-overview">
            Overview
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The advertisement system includes built-in Google Tag Manager integration, allowing your marketing team to
            track ad performance in GA4 and other analytics tools. All ad interactions automatically push events to
            GTM's <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">dataLayer</code>.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="events-tracked">
            Events Tracked
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The system automatically tracks three types of ad interactions:
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">1. Ad Impressions</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">Fired when an ad is displayed to users:</p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Banner ads</strong>: When the banner becomes visible
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Modal ads</strong>: When the modal opens
            </li>
          </ul>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            <strong className="font-semibold text-zinc-900">Event name:</strong>{' '}
            <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_impression</code>
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">2. Ad Clicks</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">Fired when users click on an advertisement:</p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Banner ads</strong>: When user clicks the banner image
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Modal ads</strong>: When user clicks the modal image or
              "View Offer" button
            </li>
          </ul>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            <strong className="font-semibold text-zinc-900">Event name:</strong>{' '}
            <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_click</code>
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">3. Ad Dismissals</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">Fired when users close a modal advertisement:</p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Clicking the X button</li>
            <li>Clicking outside the modal</li>
            <li>Clicking "View Offer" (which closes the modal)</li>
            <li>Includes whether user checked "Don't show this again"</li>
          </ul>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            <strong className="font-semibold text-zinc-900">Event name:</strong>{' '}
            <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_dismiss</code>
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Data Captured</h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Each event includes comprehensive data about the advertisement:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">ad_id</strong>: Unique advertisement identifier
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">ad_name</strong>: Internal name for the ad
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">ad_type</strong>: Either "modal" or "banner"
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">ad_placement</strong>: Location (e.g., "left-sidebar",
              "modal")
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_source</strong>: UTM source parameter
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_medium</strong>: UTM medium parameter
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_campaign</strong>: UTM campaign parameter
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_content</strong>: UTM content parameter
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">utm_term</strong>: UTM term parameter
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">click_url</strong>: Destination URL (for click events)
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">dont_show_again</strong>: Dismissal preference (for
              dismiss events)
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="setting-up-gtm">
            Setting Up GTM
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Your marketing team needs to configure GTM to capture these events:
          </p>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Step 1: Create Custom Event Triggers</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">In GTM, create three triggers:</p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Trigger Type: Custom Event</li>
            <li>
              Event names: <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_impression</code>,{' '}
              <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_click</code>,{' '}
              <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_dismiss</code>
            </li>
          </ul>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Step 2: Create Data Layer Variables</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">Create variables in GTM for each data point:</p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              DLV - Ad ID → <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_id</code>
            </li>
            <li>
              DLV - Ad Name → <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_name</code>
            </li>
            <li>
              DLV - Ad Type → <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">ad_type</code>
            </li>
            <li>
              DLV - UTM Campaign → <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">utm_campaign</code>
            </li>
            <li>And so on for each field you want to track</li>
          </ul>

          <h4 className="text-lg font-semibold text-zinc-700 mb-3 mt-6">Step 3: Create GA4 Event Tags</h4>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Set up GA4 tags to send events to Google Analytics:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Tag Type: GA4 Event</li>
            <li>Map each data layer variable to GA4 event parameters</li>
            <li>Configure triggers to fire on the appropriate custom events</li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="testing-gtm">
            Testing GTM Integration
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            To verify events are firing correctly, open your browser console and run:
          </p>
          <pre className="bg-zinc-100 rounded p-4 overflow-x-auto mb-4">
            <code className="font-mono text-sm text-zinc-900">
              {`// Check if dataLayer exists
console.log(window.dataLayer);

// Monitor new events
window.dataLayer.push = new Proxy(window.dataLayer.push, {
  apply(target, thisArg, argumentsList) {
    console.log('GTM Event:', argumentsList[0]);
    return target.apply(thisArg, argumentsList);
  }
});`}
            </code>
          </pre>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Then interact with ads and watch the console for event logs.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="ga4-reports">
            GA4 Reports
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Once configured, you can create reports in GA4 to analyze:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Ad Performance</strong>: Impressions, clicks, and CTR by
              campaign
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Placement Analysis</strong>: Which ad placements perform
              best
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Targeting Effectiveness</strong>: How keyword/index
              targeting affects performance
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Dismissal Behavior</strong>: How often users dismiss ads
              permanently
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Conversion Attribution</strong>: Track which ads lead to
              conversions using UTM parameters
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Best Practices</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Consistent UTM naming</strong>: Use standardized campaign
              names across all ads
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Test in GTM Preview mode</strong>: Verify events before
              publishing
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Use GA4 DebugView</strong>: Monitor real-time events
              during testing
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Set up custom reports</strong>: Create dashboards for easy
              ad performance tracking
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Document your setup</strong>: Keep notes on GTM
              configuration for team reference
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Detailed Documentation</h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            For complete setup instructions, technical details, and troubleshooting guidance, see the{' '}
            <code className="bg-zinc-100 rounded px-2 py-1 font-mono text-sm">GTM_INTEGRATION.md</code> file in the
            project root. This document includes:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Detailed event data structures</li>
            <li>Step-by-step GTM configuration instructions with screenshots</li>
            <li>CSV format specifications for bulk imports</li>
            <li>Troubleshooting common integration issues</li>
            <li>Advanced GA4 reporting examples</li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="opensearch-dashboard">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            OpenSearch Dashboard
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Overview</h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The OpenSearch Dashboard tab provides a live preview of your search interface. This allows you to test
            changes and see how users experience the search functionality.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Features</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Live Preview</strong>: View the actual search interface in
              an embedded iframe
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Environment-Aware</strong>: Automatically shows the dev or
              production URL based on your settings
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Open in New Tab</strong>: Click the button to view the
              full interface in a separate browser tab
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Real-Time Testing</strong>: Test search queries, filters,
              and see promoted results in action
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Using the Dashboard</h3>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              Navigate to the <strong className="font-semibold text-zinc-900">OpenSearch Dashboard</strong> tab
            </li>
            <li>The live search interface will load in the iframe</li>
            <li>Perform searches to test your configuration changes</li>
            <li>Click "Open in New Tab" to view in a full browser window</li>
          </ol>

          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            <strong className="font-semibold text-zinc-900">Note</strong>: The dashboard shows the live environment, so
            configuration changes you make in the admin panel will be reflected here once saved.
          </p>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="best-practices">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">Best Practices</h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Search Weights</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Start with moderate weights and adjust based on search result quality</li>
            <li>Give title fields higher weights than content fields</li>
            <li>Test changes with representative search queries</li>
            <li>Keep weights balanced - avoid extreme values</li>
            <li>Document your changes for future reference</li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Results Management</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Use specific, relevant keywords for better targeting</li>
            <li>Keep titles concise and descriptive</li>
            <li>Set appropriate date ranges to avoid outdated results</li>
            <li>Monitor performance and adjust priorities as needed</li>
            <li>Use the Force Result feature to surface high-quality existing content</li>
            <li>Use Priority results when you want content at the top without any promotional appearance</li>
            <li>Use Sponsored/Promoted for the promoted section (max 3 combined)</li>
            <li>Regularly review and clean up inactive or outdated managed results</li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Advertisements</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>Use standard ad sizes for better compatibility (300x600, 728x90)</li>
            <li>Optimize images for fast loading - compress without sacrificing quality</li>
            <li>Always include UTM parameters to track ad performance</li>
            <li>Test ads with different keywords and content types to find the best targeting</li>
            <li>Set realistic date ranges and update campaigns regularly</li>
            <li>Use clear, compelling imagery that aligns with your brand</li>
            <li>Monitor click-through rates and adjust priority/targeting as needed</li>
            <li>Don't overuse modal ads - they can be intrusive if shown too frequently</li>
          </ul>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="index-statistics">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">
            Index Statistics
          </h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="index-statistics-overview">
            Overview
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The Index Statistics section provides a comprehensive view of all OpenSearch indexes, including document
            counts, storage sizes, and detailed breakdowns by site and category. This feature helps administrators
            monitor the health and growth of search indexes and export data for reporting purposes.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="index-statistics-tabs">
            Available Tabs
          </h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Overview</strong>: Summary cards showing total indexes,
              total documents, total storage, and category counts. Also displays a breakdown table of documents by
              category (WordPress, Digital Library, Materials Data, etc.).
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">All Indexes</strong>: Complete list of all OpenSearch
              indexes with document counts, storage sizes, categories, and health status indicators (green/yellow/red).
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">WP Pages</strong>: Breakdown of WordPress pages by site
              name (ASM International, Heat Treating Progress, etc.).
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Products</strong>: Breakdown of products by category.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Videos</strong>: Breakdown of videos by site name.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Events</strong>: Breakdown of events by site name.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">News</strong>: Breakdown of ASM news articles by category.
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6" id="excel-export">
            Excel Export
          </h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            Click the <strong className="font-semibold text-zinc-900">"Export to Excel"</strong> button to download a
            comprehensive Excel workbook containing multiple sheets:
          </p>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Index Summary</strong>: All indexes with document counts,
              storage sizes, categories, health status, and status.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Summary by Category</strong>: Aggregated counts by
              category (WordPress, Digital Library, Materials Data, etc.) with totals.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">WP Pages by Site</strong>: Page counts per WordPress site.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Products by Category</strong>: Product counts per
              category.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Videos by Site</strong>: Video counts per WordPress site.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Events by Site</strong>: Event counts per WordPress site.
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">News by Category</strong>: ASM news article counts per
              category.
            </li>
          </ul>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            The exported file is named with a timestamp (e.g.,{' '}
            <code className="bg-zinc-100 px-1.5 py-0.5 rounded text-sm font-mono">
              opensearch_index_statistics_2025-12-05T10-30-00.xlsx
            </code>
            ) for easy identification and version tracking.
          </p>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Using Index Statistics</h3>
          <ol className="list-decimal list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              Navigate to the <strong className="font-semibold text-zinc-900">Index Statistics</strong> tab in the
              sidebar
            </li>
            <li>Review the Overview tab for a high-level summary of your search data</li>
            <li>Use the tab navigation to explore specific content types (Pages, Products, Videos, etc.)</li>
            <li>Click "Refresh" to reload the latest statistics from OpenSearch</li>
            <li>Click "Export to Excel" to download a comprehensive report for offline analysis or sharing</li>
          </ol>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-6">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                  <path
                    fillRule="evenodd"
                    d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
                    clipRule="evenodd"
                  />
                </svg>
              </div>
              <div className="ml-3">
                <p className="text-sm text-blue-700">
                  <strong>Tip:</strong> The statistics are fetched live from OpenSearch. Use the Refresh button after
                  significant indexing operations to see updated counts.
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="bg-white border border-zinc-200 rounded-lg p-6 mb-8" id="troubleshooting">
          <h2 className="text-2xl font-semibold text-zinc-900 mb-4 pb-2 border-b border-zinc-200">Troubleshooting</h2>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Common Issues</h3>
          <ul className="list-disc list-inside space-y-2 text-zinc-700 ml-4 mb-4">
            <li>
              <strong className="font-semibold text-zinc-900">Changes not taking effect</strong>: Ensure you clicked
              "Save Configuration" and wait for the success message
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Promoted results not appearing</strong>: Check that the
              result is active and the date range is current
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">Search results quality decreased</strong>: Review recent
              weight changes and consider reverting to defaults
            </li>
            <li>
              <strong className="font-semibold text-zinc-900">CSV import failed</strong>: Verify the CSV format matches
              the expected structure
            </li>
          </ul>

          <h3 className="text-xl font-semibold text-zinc-900 mb-3 mt-6">Getting Help</h3>
          <p className="text-base text-zinc-700 mb-4 leading-relaxed">
            If you encounter issues not covered in this guide, check the browser console for error messages or contact
            your system administrator.
          </p>
        </section>
      </div>
    </div>
  );
};

export default UserGuide;
